<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5
        v-if="canAddImage || images.length"
        class="mb-0"
      >
        {{ $t(`components.sidebar.Tabs.Images.${view}`) }}
      </h5>
      <div v-else-if="view === 'top'">
        {{ $t("components.sidebar.Tabs.Images.noImages") }}
      </div>

      <div
        v-b-tooltip.hover.left
        :title="tooltipText"
        style="min-width: 1px;"
      >
        <b-button
          v-if="canAddImage && isActive"
          :disabled="!hasCyclomediaAccount || alreadyGenerated || images.length >= 8 ||loading"
          variant="primary"
          size="sm"
          class="d-flex align-items-center"
          @click="getFromCyclomedia"
        >
          {{ $t("components.sidebar.Tabs.Images.generate") }}
        </b-button>
      </div>

      <input
        ref="fileInput"
        type="file"
        hidden
        @change="onFileChange"
      >
    </div>

    <div
      class="thumbnail-grid"
    >
      <ConfirmationDialog
        ref="confirmDialogImages"
        :title="$t('components.sidebar.Tabs.Images.confirmation.title')"
        :message="$t('components.sidebar.Tabs.Images.confirmation.message')"
        :confirm-text="$t('components.sidebar.Tabs.Images.confirmation.delete')"
        :cancel-text="$t('components.sidebar.Tabs.Images.confirmation.cancel')"
      />

      <div
        v-for="(image, idx) in images"
        :key="idx"
        class="thumbnail"
        :class="{ 'is-primary': image.primary }"
        @click="openCarousel(image, idx)"
      >
        <img
          :src="image.url"
          :alt="'Image ' + idx"
        >
        <span
          v-if="image.primary"
          class="primary-badge"
        >
          {{ $t("components.sidebar.Tabs.Images.primary") }}
        </span>
      </div>
      <div
        v-if="canAddImage && isActive && images.length < 8"
        class="thumbnail add-thumbnail"
        @click="triggerFileInput"
      >
        <span class="add-icon">
          <i :class="loading ? 'far fa-spinner fa-spin' : 'fas fa-plus'" />
        </span>
      </div>
      <span v-if="!isActive && !images.length && canAddImage">
        {{ $t("components.sidebar.Tabs.Images.noImages") }}
      </span>
    </div>

    <b-modal
      v-model="showModal"
      :title="$t('components.sidebar.Tabs.Images.gallery')"
      hide-header
      centered
    >
      <div
        :class="[
          'd-flex mb-2',
          currentImage?.primary
            ? 'justify-content-between'
            : 'justify-content-end',
        ]"
      >
        <span
          v-if="currentImage?.primary"
          class="primary-modal-badge"
        >
          {{ $t("components.sidebar.Tabs.Images.primary") }}
        </span>


        <div
          v-if="canAddImage && currentImage && isActive"
        >
          <b-dropdown
            :text="$t('components.sidebar.Tabs.Images.options')"
            variant="primary"
            size="sm"
            right
          >
            <b-dropdown-item @click="deleteCurrentFile">
              <span> <i class="far fa-fw fa-trash-alt mr-2" />{{ $t("components.sidebar.Tabs.Images.deleteImage") }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="setAsPrimary">
              <span><i class="far fa-fw fa-star mr-2" />
                {{ $t("components.sidebar.Tabs.Images.setPrimary") }}</span>
            </b-dropdown-item>
            <b-dropdown-item disabled>
              <span><i class="far fa-fw fa-pencil mr-2" />
                {{ $t("components.sidebar.Tabs.Images.drawOnImage") }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
          <span>
            <i
              class="fas fa-lg fa-times cursor-pointer ml-4 mt-2"
              @click="showModal = false"
            />
          </span>
        </div>
      </div>

      <b-carousel
        :id="`${id}-modal-carousel`"
        v-model="currentSlide"
        :controls="images.length > 1"
        :interval="0"
        class="fixed-carousel"
        fade
        loading
      >
        <div
          v-if="loading"
          class="overlay"
        >
          <i class="fas fa-spinner fa-spin" />
        </div>
        <b-carousel-slide
          v-for="(image, idx) in images"
          :key="'modal-' + idx"
          :img-src="image.url"
        />
      </b-carousel>
      <template #modal-footer>
        <div
          class="d-flex flex-column"
          style="width: 300px;"
        >
          <div class="d-flex">
            <span class="w-50 text-right font-weight-bolder mr-2">{{ $t("components.sidebar.Tabs.Images.metadata.source") }}:</span>
            <span>{{ $t(`components.sidebar.Tabs.Images.metadata.${currentImage?.metadata?.source }`)|| 'N/A' }}</span>
          </div>
          <div class="d-flex">
            <span class="w-50 text-right font-weight-bolder mr-2">{{ $t("components.sidebar.Tabs.Images.metadata.uploadDate") }}:</span> <span>{{ formatDate(currentImage?.metadata?.dateOfUpload) || 'N/A' }}</span>
          </div>
          <div class="d-flex">
            <span class="w-50 text-right font-weight-bolder mr-2">{{ $t("components.sidebar.Tabs.Images.primary") }}:</span>
            <span> {{ currentImage?.primary ? $t('yes'): $t('no') }}</span>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'ImageGallerySection',
  components: { ConfirmationDialog },
  props: {
    loading: { type: Boolean, required: true },
    images: { type: Array, required: true },
    title: { type: String, required: true },
    id: { type: String, required: true },
    view: { type: String, required: true },
    uuid: { type: String, required: true },
    hasCyclomediaAccount: { type: Boolean, default: false },
    alreadyGenerated: { type: Boolean, default: false },
  },
  data() {
    return {
      currentSlide: 0,
      currentImage: null,
      showModal: false,
    }
  },
  computed: {
    ...mapGetters('planmode', ['isActive', 'getChargingPointByUuid']),
    ...mapGetters('currentUser', ['getCurrentUserRole']),
    tooltipText() {
      if (this.hasCyclomediaAccount) {
        return this.alreadyGenerated ? this.$t('components.sidebar.Tabs.Images.tooltips.generated') : this.$t('components.sidebar.Tabs.Images.tooltips.generate')
      }

      return this.$t('components.sidebar.Tabs.Images.tooltips.generateNoAccount')
    },
    chargingpoint () {
      return this.getChargingPointByUuid({ uuid: this.uuid })
    },
    isRealized () {
      return this.chargingpoint.properties.status === 'realized'
    },
    stakeholderUuid () {
      return this.chargingpoint.properties.stakeholders?.find(s => s.category === 'cpo')?.uuid
    },
    userIdentity () {
      return this.$auth.user['https://evmaps.nl/identity']
    },
    userIdentityCpoUuid () {
      const isCpo = this.userIdentity?.category === 'cpo'
      return isCpo && this.userIdentity.uuid
    },
    canAddImage () {
      if (this.getCurrentUserRole !== 'cpo') return true
      // cpo check
      return (this.userIdentityCpoUuid === this.stakeholderUuid) || (!this.stakeholderUuid && !this.isRealized)
    },
  },

  watch: {
    currentSlide(idx) {
      this.currentImage = this.images[idx] || null
    },
    images(newImages) {
      if (!newImages.length) {
        this.resetCarousel()
      } else if (this.currentSlide >= newImages.length) {
        this.currentSlide = newImages.length - 1
        this.currentImage = newImages[this.currentSlide]
      } else {
        this.currentImage = newImages[this.currentSlide]
      }
    },
  },
  methods: {
    getFromCyclomedia() {
      this.$emit('get-from-cyclomedia', this.view)
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    formatDate(date) {
      if (!date) return ''

      return new Date(date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    openCarousel(image, index) {
      this.showModal = true
      this.currentSlide = index
      this.currentImage = image
    },
    onFileChange(event) {
      const file = event.target.files[0]
      if (!file) return

      this.$emit(`update-images-${this.view}`, file)
      this.$refs.fileInput.value = ''
    },
    async deleteCurrentFile() {
      if (!this.currentImage) return

      const confirmed = await this.$refs.confirmDialogImages.open()
      if (!confirmed) return

      this.$emit(`delete-image-${this.view}`, this.currentImage)
    },
    setAsPrimary() {
      if (!this.currentImage) return
      this.$emit(`set-primary-${this.view}`, this.currentImage)
    },
    resetCarousel() {
      this.showModal = false
      this.currentSlide = 0
      this.currentImage = null
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 2rem;
  z-index: 99
}

.thumbnail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  .thumbnail {
    position: relative;
    width: 100%;
    padding-top: 100%;
    cursor: pointer;
    border-radius: 6px;
    border: 2px solid transparent;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;

    &.is-primary {
      border-color: #28a745;
    }

    >img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.2s;
    }

    &:hover {
      >img {
        transform: scale(1.1);
      }
    }
    &.add-thumbnail  {
      &:hover {
      i {
        transition: all 0.2s ease-in-out;
        transform: scale(1.2);
        }
      }
      .add-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 2rem;
        color: #aaa;
      }
    }

    .primary-badge {
      position: absolute;
      top: 6px;
      left: 6px;
      background: #28a745;
      color: #fff;
      font-size: 0.7rem;
      padding: 2px 6px;
      border-radius: 4px;
    }
  }
}

.primary-modal-badge {
  background: #28a745;
  color: #fff;
  font-size: 1rem;
  padding: 2px 6px;
  border-radius: 4px;
}

::v-deep(.close) {
  color: #fff;
}

/* Modal Styles */
::v-deep(.modal-dialog) {
  max-width: 700px;
  max-height: 700px;
  width: 700px;
  height: 700px;
  display: flex;
  align-items: center;
}

::v-deep(.modal-content) {
  height: 700px;
}

::v-deep(.modal-body) {
  overflow: hidden;
}

::v-deep(.carousel) {
  height: calc(100% - 50px);
  padding: 2.5rem;
}

::v-deep(.carousel-inner) {
  height: 100%;
  display: flex;
  align-items: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: scale-down;
  }
}

::v-deep(.carousel-control-prev-icon),
::v-deep(.carousel-control-next-icon) {
  filter: invert(0) brightness(0);
}

::v-deep(.carousel-control-prev),
::v-deep(.carousel-control-next) {
  width: auto;
  top: 50%;
  transform: translateY(-50%);
}

::v-deep(.carousel-indicators li) {
  background-color: black !important;
}
</style>
